import '@vio/atlas/components/designSystem/helpers/Box/Box.styles.css'

import React, {useRef} from 'react'
import {Provider} from 'react-redux'
import AppProviders from 'AppProviders'
import {useCreateServerToggle} from 'hooks/useServerToggle'
import {AppProps} from 'next/app'
import dynamic from 'next/dynamic'
import Error, {ErrorProps} from 'next/error'
import Head from 'next/head'
import Script from 'next/script'
// eslint-disable-next-line no-restricted-imports
import {toggle} from 'opticks'
import {initializeStore, RootState} from 'store'
import {CommonPageProps} from 'utils/server/defaultGetServerSideProps'

import {useThemeOverride} from '@vio/core/src/_web/brand/hooks/useThemeOverride'
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook -- Runs outside the Redux provider
import {defaultBrand} from '@vio/core/src/brand/config'
import {ServerExperimentsContext} from '@vio/core/src/experiments/context/ServerExperimentsContext'
import {ScenarioSimulatorDeferredLoader} from '@vio/debug-panel/src/utils/scenarioSimulator/ScenarioSimulatorDeferredLoader'

/**
 * Global components & hooks, loaded client-side only to ease migration from CRA
 */
const AppGlobals = dynamic(
  () =>
    import(
      /* webpackPrefetch: true */
      '../AppGlobals'
    ),
  {ssr: false}
)

const AppOrError = (props: AppProps<CommonPageProps | ErrorProps>) => {
  if ('statusCode' in props.pageProps) {
    return <Error {...props.pageProps} />
  } else {
    return <App {...(props as AppProps<CommonPageProps>)} />
  }
}

const scenarioSimulatorLoader = () => import('mocks/initializeSimulator')

const App = ({Component, pageProps}: AppProps<CommonPageProps>) => {
  const {
    brand = defaultBrand,
    shouldLoadSimulator = false,
    preloadedMessages,
    preloadedCalendarStrings
  } = pageProps

  const preloadedState = pageProps.preloadedReduxState as unknown as RootState

  const serverToggle = useCreateServerToggle({preloadedState, pageProps})
  const experimentContextRef = useRef({
    isCachedPage: false,
    serverToggle,
    presetExperiments: pageProps.experiments
  })

  const storeRef = useRef<ReturnType<typeof initializeStore> | null>(null)
  if (!storeRef.current) {
    storeRef.current = initializeStore(preloadedState, {
      toggle: serverToggle ?? toggle
    })
  }

  const themeOverride = useThemeOverride(process.env.NEXT_PUBLIC_ENV || '')
  const cssThemeOverridesHost = process.env.NEXT_PUBLIC_CSS_THEME_OVERRIDES_URL

  const {cookieConsent} = brand.capabilities.compliance

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="icon"
          href={`${brand.siteAddresses.address}/static/brands/${brand.code}_favicon.png?v=2`}
          sizes="any"
        />
        <link rel="stylesheet" href={`/assets/css/${brand.theme}.css`} />

        {Boolean(themeOverride) && cssThemeOverridesHost && (
          <link
            rel="stylesheet"
            href={`${cssThemeOverridesHost}/${themeOverride}.css`}
            id="theme-override"
          />
        )}

        <title>{`Checkout - ${brand.name}`}</title>
        <meta name="description" content={brand.metaDescription} />

        {cookieConsent.enabled ? (
          <>
            <script
              type="text/javascript"
              id="osano-consent-mode"
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer ||[];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent','default',{
                'ad_storage':'denied',
                'analytics_storage':'denied',
                'ad_user_data':'denied',
                'ad_personalization':'denied',
                'personalization_storage':'denied',
                'functionality_storage':'granted',
                'security_storage':'granted',
                'wait_for_update': 500
              });
              gtag("set", "ads_data_redaction", true);
            `
              }}
            />
            <style>{`.osano-cm-dialog,.osano-cm-widget{display: none;}`}</style>
          </>
        ) : null}
      </Head>
      {cookieConsent.enabled && (
        <>
          <Script
            id="osano-cross-device-consent"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              ;(function(w,o,d){w[o]=w[o]||function(){w[o][d].push(arguments)};w[o][d]=w[o][d]||[]})(window,'Osano','data');
              if(window.osanoMobileAppIdentifier) {
                window.Osano('userData', window.osanoMobileAppIdentifier);
              }`
            }}
          />
          <Script
            id="osano-cmp"
            strategy="afterInteractive"
            src={`https://cmp.osano.com/AzZuEAUQXGtUb3YcB/${cookieConsent.config.id}/osano.js`}
          />
        </>
      )}
      <ServerExperimentsContext.Provider value={experimentContextRef.current}>
        <Provider store={storeRef.current}>
          <AppProviders
            preloadedMessages={preloadedMessages}
            preloadedCalendarStrings={preloadedCalendarStrings}
          >
            <AppGlobals />
            <ScenarioSimulatorDeferredLoader
              initializer={scenarioSimulatorLoader}
              shouldLoadSimulator={shouldLoadSimulator}
            >
              <Component {...pageProps} />
            </ScenarioSimulatorDeferredLoader>
          </AppProviders>
        </Provider>
      </ServerExperimentsContext.Provider>
    </>
  )
}

export default AppOrError
